/* THE MOVES PANEL — pick a ready-made animation, press Apply.

   It sits under the property rows in the Motion inspector, because that is
   where someone already is when they have decided something should move. What
   it writes is ordinary keyframes on the tracks below it, so the panel is a
   shortcut rather than a mode: nothing here has to be undone before editing by
   hand, and nothing remembers that a move was used.

   Gated whole. canUseMotion() false returns null and no control appears at
   all — the same posture as the stopwatch in ui/timeline-view.js, and for the
   same reason: the dock right below already carries the licensing sentence and
   its call to action, so a second locked control here would only repeat it. */

import { ANIMATABLE, animatableProps } from '../core/anim.js';
import { GROUP_LABELS, MOVE_GROUPS, minLength, moveById, movesFor } from '../core/moves.js';
import { clamp } from '../core/util.js';
import { track } from '../services/analytics.js';
import { canUseMotion } from '../services/limits.js';
import { requestRender } from '../state/bus.js';
import { applyMove, clearAllTracks, clearMoveTracks, isPreviewing, moveIsOn, previewMove, previewingObject, stopPreview } from '../state/moves.js';
import { S, el } from '../state/store.js';
import { frameDur } from '../state/timeline.js';
import { infoToggle, numRow, panel, segRow, selectRow } from './controls.js';
import { renderRight } from './rail-right.js';
import { toast } from './toast.js';

/* Session state, not document state: which move is showing is the same kind
   of thing as where the playhead is — how you are looking at the project, not
   part of it. So it is deliberately outside the undo snapshot, and it resets
   when the app is reloaded. The panel is rebuilt wholesale on every render,
   which is why this cannot live inside the function. */
let choice = { id: null, length: null, start: 'playhead' };

const label = p => (ANIMATABLE[p]?.label || p).toLowerCase();

/** A list like "position and opacity". */
function readList(props) {
  const names = props.map(label);
  return names.length < 2 ? names.join('') : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
}

/**
 * The panel for this object, or null when there is nothing to offer.
 * @returns {HTMLElement|null}
 */
export function movesPanel(o) {
  /* A preview belongs to the object it started on. Selecting a different one,
     or losing the panel altogether, ends it — there is no selection event on
     the bus to hang that on, so it is done here, where the rail is rebuilt.
     A rebuild for the SAME object leaves a running preview alone, which is
     what lets Apply and the value fields redraw without cutting it short. */
  if (isPreviewing() && previewingObject() !== o) stopPreview();
  if (!o || !canUseMotion().ok) return null;
  const list = movesFor(animatableProps(o), o);
  if (!list.length) return null;                 // a camera, and nothing else today

  /* The remembered choice may not be legal on this object — every move is
     offered per object, so switching from a text layer to an inset can take
     the selection away. Fall back to the first rather than showing an empty
     control. */
  const move = list.find(m => m.id === choice.id) || list[0];
  const loop = move.span === 'duration';
  const floor = minLength(move, frameDur());
  const len = loop ? S.timeline.duration
                   : clamp(choice.length == null ? move.length : choice.length, floor, 30);

  const p = panel('Moves', list.length + ' ready-made', { key: 'Moves/animate' });

  /* One control for the whole catalogue: [null, 'Entrances'] starts an
     optgroup, so the four families separate without a second widget. Moves
     this object cannot honour are left out rather than shown disabled — an
     option that refuses when you click it is worse than one that was never
     offered. */
  const opts = [];
  for (const g of MOVE_GROUPS) {
    const inGroup = list.filter(m => m.group === g);
    if (!inGroup.length) continue;
    opts.push([null, GROUP_LABELS[g] || g]);
    for (const m of inGroup) opts.push([m.id, m.name]);
  }
  p.body.appendChild(selectRow('Move', move.id, opts, v => {
    choice = { ...choice, id: v, length: null };   // a new move brings its own default length
    renderRight();
  }));
  p.body.appendChild(el('p', 'note', move.hint));

  if (loop) {
    p.body.appendChild(el('p', 'note',
      `Fills the timeline — ${S.timeline.duration.toFixed(1)}s — and ends where it started, so it loops cleanly. `
      + 'Change the duration later and it needs applying again.'));
  } else {
    p.body.appendChild(numRow('Length', len, 0.1, v => {
      choice = { ...choice, length: clamp(v, floor, 30) };
      renderRight();
    }, 's'));
    /* Short labels: "At the playhead" wraps onto two lines in the rail's
       width, which makes the row twice as tall as the ones around it. */
    p.body.appendChild(segRow('Starts', choice.start,
      [['playhead', 'Playhead'], ['zero', 'At 0s']],
      v => { choice = { ...choice, start: v }; }));
  }

  const why = infoToggle(
    'A move writes ordinary keyframes — drag them, re-ease them or delete them like any others. '
    + 'It replaces the keyframes on the properties it animates (' + readList(move.needs) + ') '
    + 'and leaves every other track alone. Values are taken from where the object is now.');
  const row = el('div', 'btnrow');
  /* Watch it before committing to it. The preview plays the identical keys
     Apply would write, so choosing between twenty-odd moves costs nothing and
     leaves no history entries behind. */
  const prev = el('button', 'btn btn--sm', isPreviewing() ? 'Stop' : 'Preview');
  prev.onclick = () => {
    if (isPreviewing()) { stopPreview(); prev.textContent = 'Preview'; return; }
    const r = previewMove(o, move, { length: len, onEnd: () => { prev.textContent = 'Preview'; } });
    if (r.ok) prev.textContent = 'Stop';
  };
  const apply = el('button', 'btn btn--sm btn--pri', 'Apply');
  apply.onclick = () => {
    const res = applyMove(o, move, { length: len, start: choice.start });
    if (!res.ok) { toast(res.reason, true); return; }
    toast(`Applied “${move.name}” — ${res.keys} keyframe${res.keys === 1 ? '' : 's'} on `
        + `${readList(res.props)}, ${res.from.toFixed(2)}–${res.to.toFixed(2)}s.`
        + (res.extended ? ` Timeline lengthened to ${S.timeline.duration.toFixed(1)}s to fit.` : ''));
    track('move_applied', { id: move.id, length: +len.toFixed(2), props: res.props.join('+') });
    renderRight();
    requestRender();
  };
  /* Short label, long tooltip. .btnrow gives each button half the rail and
     nothing there wraps, so "Stop animating position and opacity" grew the
     button straight out past the panel edge and over Apply. */
  const clear = el('button', 'btn btn--sm btn--gh', 'Clear');
  clear.title = 'Stops animating ' + readList(move.needs)
              + ' — the properties this move writes. Every other track is left alone.';
  clear.disabled = !moveIsOn(o, move);
  clear.onclick = () => {
    const res = clearMoveTracks(o, move);
    if (res.ok) toast('Stopped animating ' + readList(res.props) + '.');
    renderRight();
    requestRender();
  };
  /* A flex child's default min-width is its content, which is why the label
     above could push the row wider than the panel instead of being cut. With
     this, the worst a long label can do is end in an ellipsis. */
  for (const btn of [prev, apply, clear])
    Object.assign(btn.style, { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });
  row.appendChild(prev);
  row.appendChild(apply);
  row.appendChild(clear);
  p.body.appendChild(row);

  /* On its own line, because it is about the object rather than the move
     above it, and because a third button in that row would squeeze all three.
     Destructive, and undoable, so it asks nothing — the same bargain Remove
     makes in the Scene panel. */
  const animated = o.anim ? Object.keys(o.anim) : [];
  const wipe = el('button', 'btn btn--sm btn--dgr btn--w', 'Clear all keyframes');
  wipe.disabled = !animated.length;
  wipe.title = animated.length
    ? `Removes every keyframe on ${o.name} — ${readList(animated)}. Ctrl+Z brings them back.`
    : 'Nothing on this object is animated yet.';
  wipe.onclick = () => {
    const res = clearAllTracks(o);
    if (res.ok) toast(`Cleared ${res.keys} keyframe${res.keys === 1 ? '' : 's'} from ${o.name}.`);
    renderRight();
    requestRender();
  };
  p.body.appendChild(wipe);

  p.body.appendChild(why.btn);
  p.body.appendChild(why.note);
  return p;
}
